import React, { useState, useEffect } from 'react';
import { parseFraction, toFractionStr } from '@/utils/fractions';

interface DimensionInputProps {
  value: string;
  fractionMode: boolean;
  onChange: (val: string) => void;
  className?: string;
  placeholder?: string;
  min?: string;
  max?: string;
}

const FRACTION_OPTIONS = [
  { label: '0', value: 0 },
  { label: '1/16', value: 1/16 },
  { label: '1/8', value: 1/8 },
  { label: '3/16', value: 3/16 },
  { label: '1/4', value: 1/4 },
  { label: '5/16', value: 5/16 },
  { label: '3/8', value: 3/8 },
  { label: '7/16', value: 7/16 },
  { label: '1/2', value: 1/2 },
  { label: '9/16', value: 9/16 },
  { label: '5/8', value: 5/8 },
  { label: '11/16', value: 11/16 },
  { label: '3/4', value: 3/4 },
  { label: '13/16', value: 13/16 },
  { label: '7/8', value: 7/8 },
  { label: '15/16', value: 15/16 },
];

/**
 * Dimension input that accepts both fractions (e.g. "96 5/16") and decimals.
 * In fraction mode: shows whole number input + fraction dropdown.
 * In decimal mode: shows a single number input.
 * Stores value as a decimal string internally.
 */
export function DimensionInput({ value, fractionMode, onChange, className, placeholder, min, max }: DimensionInputProps) {
  const [displayVal, setDisplayVal] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Fraction mode split state
  const [wholeVal, setWholeVal] = useState('');
  const [fracVal, setFracVal] = useState(0);

  // Sync from parent value
  useEffect(() => {
    if (!value || value === '') {
      if (!isFocused) setDisplayVal('');
      setWholeVal('');
      setFracVal(0);
      return;
    }
    const num = parseFloat(value);
    if (isNaN(num)) return;

    // Update fraction split
    const whole = Math.floor(num);
    const remainder = num - whole;
    // Find closest fraction option
    const closest = FRACTION_OPTIONS.reduce((prev, curr) =>
      Math.abs(curr.value - remainder) < Math.abs(prev.value - remainder) ? curr : prev
    );
    setWholeVal(whole > 0 ? String(whole) : '');
    setFracVal(closest.value);

    // Update decimal display
    if (!isFocused) {
      if (fractionMode) {
        setDisplayVal(toFractionStr(num));
      } else {
        setDisplayVal(value);
      }
    }
  }, [value, fractionMode, isFocused]);

  // Fraction mode: combine whole + fraction and emit
  const emitFractionValue = (whole: string, frac: number) => {
    const w = parseInt(whole) || 0;
    const total = w + frac;
    if (total > 0) {
      onChange(String(total));
    } else if (whole === '') {
      onChange('');
    } else {
      onChange('0');
    }
  };

  const handleWholeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    setWholeVal(raw);
    emitFractionValue(raw, fracVal);
  };

  const handleFracChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const frac = parseFloat(e.target.value);
    setFracVal(frac);
    emitFractionValue(wholeVal, frac);
  };

  // Decimal mode handlers
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setDisplayVal(raw);
    if (!fractionMode && !raw.includes('/')) {
      onChange(raw);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (!displayVal.trim()) {
      onChange('');
      return;
    }
    if (displayVal.includes('/')) {
      const parsed = parseFraction(displayVal);
      if (!isNaN(parsed)) onChange(String(parsed));
    } else {
      const parsed = parseFloat(displayVal);
      if (!isNaN(parsed)) onChange(String(parsed));
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (fractionMode && value) {
      const num = parseFloat(value);
      if (!isNaN(num)) setDisplayVal(toFractionStr(num));
    } else {
      setDisplayVal(value || '');
    }
  };

  if (fractionMode) {
    return (
      <div className="flex gap-1">
        <input
          type="text"
          inputMode="numeric"
          value={wholeVal}
          onChange={handleWholeChange}
          onWheel={(e) => e.currentTarget.blur()}
          className={className}
          placeholder={placeholder || 'Inches'}
          style={{ flex: '1 1 0' }}
        />
        <select
          value={fracVal}
          onChange={handleFracChange}
          className={className}
          style={{ flex: '0 0 auto', width: 'auto', minWidth: '70px', paddingRight: '4px' }}
        >
          {FRACTION_OPTIONS.map(opt => (
            <option key={opt.label} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <input
      type="number"
      inputMode="decimal"
      step="0.0001"
      min={min}
      max={max}
      value={displayVal}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onWheel={(e) => e.currentTarget.blur()}
      className={className}
      placeholder={placeholder}
    />
  );
}
